/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useAppAssetsWithSrc } from "Theme/EVENTS_ASSETS";
import HidePasswordIcon from "assets/Icons/HidePassword";
import ShowPasswordIcon from "assets/Icons/ShowPassword";
import { Button, FontAwesomeIcon, Input, Stack, Text } from "components";
import LoadingScreen from "components/LoadingScreen";
import LOCAL_STORAGE_KEYS, {
  SESSION_STORAGE_KEYS,
} from "constants/STORAGE_KEYS";
import useEventRouting from "hooks/useEventRouting";
import useTryCatch from "hooks/useTryCatch";
import loginUser from "lib/loginApi";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import ROUTES from "routes";
import {
  redirectToEventSwitcher,
  redirectToNoAccessPage,
} from "utils/eventSwitcher";
import { useAuth, useOrgData, useUI } from "state";
import { useNavigationPostLogin } from "state/AuthContext";
import { useGlobal } from "state/GeneralContext";
import { useUser } from "state/UserContext";
import { ScreenPage } from "templates";
import sha512 from "utils/encode";
import { pushToDataLayer } from "utils/gtm";
import useLocalStorage from "utils/useLocalStorage";

const LoginPage = () => {
  const { t } = useTranslation();
  const [token, setToken] = useLocalStorage(LOCAL_STORAGE_KEYS.token, null);
  const [id, setId] = useLocalStorage(LOCAL_STORAGE_KEYS.id, null);
  const [_name, setName] = useLocalStorage(LOCAL_STORAGE_KEYS.name, null);
  const [_eventCode, setEventCode] = useLocalStorage(
    LOCAL_STORAGE_KEYS.eventCode,
    null,
  );
  const [_isActive, setIsActive] = useLocalStorage(
    LOCAL_STORAGE_KEYS.isActive,
    null,
  );
  const [_registerPageStage, setRegisterPageStage] = useLocalStorage(
    LOCAL_STORAGE_KEYS.registerPageStage,
    null,
  );

  const { setEvents, events } = useGlobal();
  const { userData, setUserData, setFormCompleted } = useUser();
  const {
    isAuthLoading,
    setAuthLoading,
    handleLogin,
    isLoggedIn,
    fetchUserData,
  } = useAuth();
  const { navigateWithEvent, eventId } = useEventRouting();
  const { colorScheme } = useUI();
  const EVENT_ASSETS = useAppAssetsWithSrc();
  const { dataContent: EVENT_CONTENT } = useOrgData();
  const loginNavigator = useNavigationPostLogin();
  const redirectFromEventIdRef = useRef(false);

  // When eventId in URL and logged in: redirect directly, no event switcher
  useEffect(() => {
    if (
      !isLoggedIn ||
      !eventId ||
      !events?.length ||
      redirectFromEventIdRef.current
    ) {
      return;
    }

    const id = Number.parseInt(eventId, 10);
    const event = events.find((e) => e.id === id);

    if (!event) {
      toast.error(t("auth.login.invalidEventError"));
      redirectToNoAccessPage();
      return;
    }

    redirectFromEventIdRef.current = true;
    fetchUserData().then((formCompleted) => {
      // Sync the context here: fetchUserData's own setFormCompleted is a
      // no-op (AuthProvider renders above UserProvider), and Dashboard
      // bounces to onboarding when the context still holds the default.
      if (formCompleted !== undefined) setFormCompleted(formCompleted);
      loginNavigator("", formCompleted, event.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, eventId, events]);

  const hashPassword = useRef("");
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const [{ username, password }, setFormData] = useState({
    username: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    username: "",
    password: "",
  });

  const handleOnChange = (e) => {
    if (error !== "") setError("");
    const { name, value } = e.target;

    setFieldErrors((prev) => ({ ...prev, [name]: "" }));

    setFormData((prev) => {
      return { ...prev, [name]: value };
    });
  };
  const handleOnSubmit = useTryCatch(
    async () => {
      // e.preventDefault();
      if (username.trim() === "" || password.trim() === "") {
        const requiredMessage = t("forms.validation.required");

        toast.error(requiredMessage);
        setFieldErrors({
          username: username.trim() === "" ? requiredMessage : "",
          password: password === "" ? requiredMessage : "",
        });

        return;
      }
      hashPassword.current = await sha512(password);
      const login = await loginUser(username.trim(), hashPassword.current);
      if (login.status === "SUCCESS") {
        // Step response (201): the account must set a new password before
        // receiving a session. Payload has NO hash_id/user_events — only a
        // short-lived step token. is_active may be false here; still route
        // to Create New Password (contract M1). The flag ships on every
        // reachable response (false on normal success) — one key everywhere.
        if (login.data.is_password_reset_required && login.data.token) {
          sessionStorage.setItem(
            SESSION_STORAGE_KEYS.stepToken,
            login.data.token,
          );
          // Kept for the client-side "new password must differ from the
          // temporary one" pre-check on the next screen.
          sessionStorage.setItem(
            SESSION_STORAGE_KEYS.tempPasswordHash,
            hashPassword.current,
          );
          // A step token is not a session. loginUser's raw localStorage.clear()
          // leaves the useLocalStorage copies stale, so null them through the
          // setters — otherwise GeneralContext still sees a session and fires
          // getEvents, whose 401 wipes storage and redirects to "/" before
          // this screen can render.
          setToken(null);
          setId(null);
          setName(null);
          setEventCode(null);
          setIsActive(null);
          setRegisterPageStage(null);
          return navigateWithEvent(ROUTES.createNewPassword);
        }
        const {
          token,
          hash_id,
          first_name,
          last_name,
          user_events,
          is_active,
        } = login.data;
        if (is_active === false) {
          setToken(login.data.token);
          setIsActive(false);
          setEventCode(login.data.event_token);
          setRegisterPageStage(login.data.token ? "verify" : "signup");
          return navigateWithEvent(ROUTES.registerLink.verifyLink);
        }

        setToken(token);
        setId(hash_id);
        setName(first_name);
        setIsActive(is_active);

        setEvents(user_events);
        setUserData((prev) => ({ ...prev, first_name, hash_id, last_name }));
        handleLogin();
        // Normal login: drop any stale step state from an abandoned
        // create-new-password flow so the force-guard can't hijack this
        // fresh session into a dead step token.
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.stepToken);
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.tempPasswordHash);
        pushToDataLayer({
          event: "login",
          screen_name: "login",
          journey_step: "login",
          action: "email_magic_link",
        });
      }
    },
    {
      setLoading: setAuthLoading,
      onError: () => setError(t("auth.login.loginError")),
    },
  );

  if (isLoggedIn && events?.length > 0 && eventId) {
    return <LoadingScreen />;
  }

  // Logged in with events: redirect to dedicated event switcher (never show event list on login page)
  if (isLoggedIn && events?.length > 0) {
    redirectToEventSwitcher();
    return <LoadingScreen />;
  }

  return (
    <ScreenPage
      desktopBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.src
          : EVENT_ASSETS.login_BG_desktopDark.src
      }
      desktopBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.fallbackSrc
          : EVENT_ASSETS.login_BG_desktopDark.fallbackSrc
      }
      mobileBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.src
          : EVENT_ASSETS.login_BG_mobileDark.src
      }
      mobileBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.fallbackSrc
          : EVENT_ASSETS.login_BG_mobileDark.fallbackSrc
      }
    >
      {isLoggedIn ? (
        <>
          <h2 className="font-bold mb-4 text-3xl md:text-4xl capitalize -text--theme-colors-text-title">
            {userData
              ? t("dashboard.welcome", {
                  name: userData.first_name + " " + userData.last_name,
                })
              : t("auth.login.welcomeTitle")}
          </h2>
          <p className="-text--theme-colors-text-body">
            {EVENT_CONTENT.login.selectEventMessage}
            <br /> <br />
            {EVENT_CONTENT.login.selectEventInstruction}
          </p>
          <Stack className="mt-8">
            <Button onClick={loginNavigator} variant="primary">
              <div className="flex items-center gap-x-s10 justify-center">
                {t("common.buttons.continue")}
                <FontAwesomeIcon name="fa-chevron-right" />
              </div>
            </Button>
          </Stack>
        </>
      ) : (
        <>
          <h2 className="font-bold mb-4 text-2xl md:text-4xl -text--theme-colors-text-title">
            {t("auth.login.welcomeTitle")}
          </h2>
          <p className="-text--theme-colors-text-body">
            {t("auth.login.subtitle")}
          </p>
          <Stack component="form" className="mt-16">
            <Stack>
              <Input
                name="username"
                id="username"
                type="text"
                value={username}
                onChange={handleOnChange}
                label={t("common.labels.email")}
                placeholder={t("auth.login.emailPlaceholder")}
                required
              />
              <span className="text-xs min-h-8 -text--theme-colors-danger-default">
                {fieldErrors.username}
              </span>
            </Stack>
            <Stack className="mt-4">
              <div className="relative" test-id="vX8mSaxM">
                <Input
                  name="password"
                  id="password"
                  type={isPasswordShown ? "text" : "password"}
                  value={password}
                  onChange={handleOnChange}
                  label={t("common.labels.password")}
                  placeholder="********"
                  required
                />
                <div
                  className="absolute end-2 inset-y-0 flex items-center bg-secondary-accent h-fit p-1 top-[40px] rounded-full"
                  onClick={() => setIsPasswordShown(!isPasswordShown)}
                  test-id="Qho4aFeQ"
                >
                  {isPasswordShown ? (
                    <HidePasswordIcon test-id="m69s0Z9r" />
                  ) : (
                    <ShowPasswordIcon test-id="ZOAkh95C" />
                  )}
                </div>
              </div>
              <div className="flex items-start justify-between gap-x-2">
                <span className="text-xs min-h-8 -text--theme-colors-danger-default">
                  {fieldErrors.password || error}
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className="!min-h-fit mt-1 text-xs whitespace-nowrap"
                  onClick={() => navigateWithEvent(ROUTES.forgotPassword)}
                >
                  {t("auth.login.forgotPassword")}
                </Button>
              </div>
            </Stack>
            <Stack className="mt-8">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isAuthLoading}
                onClick={handleOnSubmit}
              >
                <div className="flex justify-center items-center gap-x-s10">
                  <Text>{t("auth.login.loginButton")}</Text>
                  <FontAwesomeIcon name="fa-chevron-right" />
                </div>
              </Button>
            </Stack>
          </Stack>
        </>
      )}
    </ScreenPage>
  );
};
export default LoginPage;
